import LoaderComponent from '@comp/LoaderComponet';
import { useSupportPhone } from '@comp/Status/SupportPhoneContext';
import {
  downloadInvoice,
  DownloadInvoiceParams,
  getTransactionStatus,
  sendRetryPayment,
  SendRetryPaymentParams,
  TranStatusResponse
} from '@root/api/getUserInfo';
import { getErrorMessage } from '@root/functions/getErrorMessage';
import { ReactComponent as ErrorIcon } from '@root/images/custom/error-icon.svg';
import { ReactComponent as SuccessIcon } from '@root/images/custom/success.svg';
import { getFormattedPriceLocale } from '@root/utils/convertors';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

const Status = () => {
  const { t } = useTranslation(['common']);
  const [searchParams] = useSearchParams();
  const id = searchParams.get('tranId');
  const [isNotFound, setIsNotFound] = useState(false);
  const [data, setData] = useState<TranStatusResponse | null>(null);

  const { setPhone } = useSupportPhone();

  useEffect(() => {
    let mounted = true;

    if (id) {
      getTransactionStatus(id)
        .then((res) => {
          if (!mounted) return;
          setData(res);
          sessionStorage.setItem('transactionData', JSON.stringify(res));
          if (res?.phone) {
            setPhone(normalizePhone(res.phone));
          } else {
            setPhone(undefined);
          }
        })
        .catch((reason) => {
          console.log(reason);
          if (reason?.response?.status === 404) {
            setIsNotFound(true);
          }
          setPhone(undefined);
        });
    }

    return () => {
      mounted = false;
      setPhone(undefined);
    };
  }, [id, setPhone]);

  const getTitleComponent = (data: TranStatusResponse) => {
    if (data.statusCode === '1000') {
      return {
        title: (
          <div className="status-title-container">
            <div className="icon-cotainer">
              <SuccessIcon />
            </div>
            {data.tranType === 'verification3ds' ? (
              <h1 className="status-title">{t('success_verify')}</h1>
            ) : (
              <h1 className="status-title">{t('success_pay')}</h1>
            )}
          </div>
        ),
        content: (
          <div className="cardProof__list-row cardProof__list-row--huge-pd">
            {data.tranType === 'verification3ds' ? (
              <p className="cardProof__list-item new-style-text">
                {t('success_verify_text')}
              </p>
            ) : (
              <>
                <p className="cardProof__list-item new-style-text">
                  {getErrorMessage(data.statusCode, t)}
                </p>
                <p className="cardProof__list-item new-style-text">
                  {getFormattedPriceLocale(data.amount)}
                </p>
              </>
            )}
          </div>
        )
      };
    }

    const errorInfo =
      data.statusDescription !== null &&
      data.statusDescription.split('&NewLine;');

    return {
      title: (
        <div className="status-title-container">
          {data.statusCode !== '5500' && (
            <>
              <div className="icon-cotainer">
                <ErrorIcon />
              </div>
              <h1 className="status-title">{t('decline')}</h1>
            </>
          )}
        </div>
      ),
      content: errorInfo ? getErrorMessage(data.statusCode, t) : null
    };
  };

  if (isNotFound) {
    return (
      <section className="cardProof">
        <form
          className="container-s container-s--min-pd"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        ></form>
      </section>
    );
  }

  if (data) {
    return (
      <section className="cardProof">
        <form
          className="container-s container-s--min-pd"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
          onSubmit={(evt) => {
            evt.preventDefault();
            window.location.replace(data.merchantLink);
          }}
        >
          {getTitleComponent(data).title}
          <div className="cardProof__list cardProof__list--no_border">
            {getTitleComponent(data).content}
          </div>
          {data.statusCode == '3800' && (
            <iframe
              style={{
                width: 'calc(100vw - 40px)',
                height: 'calc(calc(100vw - 40px) / 16 * 9)',
                maxWidth: '550px',
                maxHeight: 'calc(550px / 16 * 9)',
                marginBottom: '40px',
                marginTop: '20px'
              }}
              src="https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2FKaspi.kz%2Fvideos%2F467534424331819%2F&width=700&show_text=false&height=700&appId"
            ></iframe>
          )}

          {data.shouldDisplayRetryButton && (
            <button
              className="new-style btn-mb"
              type="submit"
              onClick={(evt) => {
                evt.preventDefault();

                const sendedData: SendRetryPaymentParams = {
                  tranId: data?.tranId
                };

                sendRetryPayment(sendedData).then((res) => {
                  const { frameUrl } = res;
                  if (frameUrl) {
                    window.location.replace(frameUrl);
                  }
                });
              }}
            >
              {t('common:retry')}
            </button>
          )}

          {data.shouldDisplayBackToSiteButton && (
            <button
              className={`new-style ${
                data.shouldDisplayRetryButton ? 'btn-without-style' : ''
              }`}
              type="submit"
            >
              {data.tranType === 'verification3ds'
                ? t('common:success_verify_btn')
                : t('common:back_to_site')}
            </button>
          )}

          {data.statusCode == '1000' && data.tranType === 'c2a' && (
            <button
              className="btn-without-style btn-mt"
              type="submit"
              onClick={(evt) => {
                evt.preventDefault();

                const sendedData: DownloadInvoiceParams = {
                  uuid: data?.tranId
                };

                downloadInvoice(sendedData).then((res) => {
                  const url = window.URL.createObjectURL(new Blob([res]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute(
                    'download',
                    `transaction_invoice_${data?.tranId}.pdf`
                  );
                  document.body.appendChild(link);
                  link.click();
                });
              }}
            >
              {t('common:download_invoice')}
            </button>
          )}
        </form>
      </section>
    );
  }

  return <LoaderComponent />;
};

export default Status;

function normalizePhone(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const digits = raw.replace(/[^\d+]/g, '');
  if (!digits) return undefined;
  return digits.startsWith('+') ? digits : `+${digits}`;
}
